import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { MapPin, Clock, Car, Accessibility, Users, Heart, ArrowRight, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';

const iconMap = {
  MapPin: MapPin,
  Clock: Clock,
  Car: Car,
  Accessibility: Accessibility,
  Users: Users,
  Heart: Heart
};

export default function Welcome() {
  const { pageData, loading, error } = usePageData('welcome');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-slate-500">Failed to load page content</p>
        </div>
      </Layout>
    );
  }

  const { hero, sections, cta, seo } = pageData;
  const whatToExpect = sections?.whatToExpect || {};
  const serviceInfo = sections?.serviceInfo || {};
  const directions = sections?.directions || {};

  return (
    <Layout>
      <SEO 
        title={seo?.title || hero?.title}
        description={seo?.description || hero?.subtitle}
        image={seo?.image || hero?.image}
        url="/welcome"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={hero?.image}
            alt={hero?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="welcome-page-title">
            {hero?.title}
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            {hero?.subtitle}
          </p>
        </div>
      </section>

      {/* What to Expect */}
      {whatToExpect?.items && whatToExpect.items.length > 0 && (
        <ContentSection bgColor="bg-white">
          <SectionHeader 
            title={whatToExpect.title || "What to Expect"}
            subtitle={whatToExpect.subtitle}
          />
          <div className="grid md:grid-cols-3 gap-8">
            {whatToExpect.items.map((item) => {
              const IconComponent = iconMap[item.icon] || Heart;
              return (
                <div 
                  key={`expect-${item.title}`}
                  className="bg-slate-50 rounded-xl p-8 text-center hover:shadow-lg transition-all border border-transparent hover:border-secondary/20"
                  data-testid={`expect-card-${item.title}`}
                >
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-primary-900 mb-3">{item.title}</h3>
                  <div 
                    className="text-slate-600 leading-relaxed prose prose-a:text-secondary"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.description) }}
                  />
                </div>
              );
            })}
          </div>
        </ContentSection>
      )}

      {/* Service Times */}
      {serviceInfo && (
        <ContentSection bgColor="bg-slate-50">
          <SectionHeader 
            title={serviceInfo.title || "Service Times & Details"}
            subtitle={serviceInfo.subtitle || "Everything you need to plan your visit"}
          />
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-soft">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-secondary font-medium">Sunday Worship</p>
                    <p className="font-heading text-2xl font-semibold text-primary-900">
                      {serviceInfo.worship?.time || "9:30 AM"}
                    </p>
                  </div>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {serviceInfo.worship?.description || "Blended worship with traditional and contemporary elements"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-8 shadow-soft">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-secondary font-medium">Sunday School</p>
                    <p className="font-heading text-2xl font-semibold text-primary-900">
                      {serviceInfo.sundaySchool?.time || "10:45 AM"}
                    </p>
                  </div>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {serviceInfo.sundaySchool?.description || "Classes for all ages following worship"}
                </p>
              </div>
            </div>
            {serviceInfo.duration && (
              <div className="text-center mt-8">
                <p className="text-slate-500 text-sm">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Service length: {serviceInfo.duration}
                </p>
              </div>
            )}
          </div>
        </ContentSection>
      )}

      {/* Directions */}
      {directions && (
        <ContentSection bgColor="bg-white">
          <SectionHeader 
            title={directions.title || "Finding Us"}
            subtitle={directions.subtitle || "We're located in the heart of downtown Geneva"}
          />
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-primary-900 mb-2">Address</h3>
                    <p className="text-slate-600 whitespace-pre-line">{directions.address?.replace(/\\n/g, '\n')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-primary-900 mb-2">Parking</h3>
                    <p className="text-slate-600">{directions.parking}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Accessibility className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-primary-900 mb-2">Accessibility</h3>
                    <p className="text-slate-600">{directions.accessibility}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-100 rounded-xl h-64 md:h-auto overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2972.5!2d-80.9486!3d41.8028!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDQ4JzEwLjEiTiA4MMKwNTYnNTUuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '250px' }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Church Location"
                />
              </div>
            </div>
          </div>
        </ContentSection>
      )}

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-primary-900"} paddingY="py-16">
          <div className="text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              {cta.title}
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto">
              {cta.description}
            </p>
            <Link
              to={cta.buttonLink || "/contact"}
              className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
              data-testid="welcome-directions-btn"
            >
              {cta.buttonText || "Get Directions"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
